<template>
  <b-container fluid id="student-tracking" class="mt-4">
    <div class="title">
      <h6>{{ lastName }}, {{ firstName }} -- {{ school }}</h6>
    </div>
    <div class="button">
      <b-button
        variant="primary"
        @click="showForm = true"
        v-if="showForm === false"
        class="m-4"
        >Add a New Record</b-button
      >
    </div>
    <div id="alerts" ref="tracking-alert">
      <b-alert
        :show="dismissCountDown"
        dismissible
        variant="success"
        @dismissed="dismissCountDown = 0"
        @dismiss-count-down="countDownChanged"
        >You have successfully added a dropout/register record!</b-alert
      >
      <b-alert :show="formError" dismissible variant="warning"
        >Something went wrong. Please refresh and try again, or contact the site
        admin.</b-alert
      >
    </div>
    <div id="tracking-form" v-if="showForm === true">
      <b-row id="student-school">
        <b-col cols="5">
          <div class="student-section">
            <div id="tracking-date" class="mt-4">
              <label for="tracking-date">Date of tracking:</label>
              <b-form-input
                name="tracking-date"
                type="date"
                v-model="tracking.date"
              ></b-form-input>
            </div>
            <div id="trainer" class="mt-4">
              <label for="student-grade"
                >Select the trainer tracking the student:</label
              >
              <b-form-select
                name="trainer"
                type="text"
                v-model="tracking.trainer_id"
                placeholder="Please select a trainer"
              >
                <option disabled value
                  >Select the trainer tracking the student</option
                >
                <option
                  v-for="(trainer, i) in trainers"
                  :key="i"
                  :value="trainer.id"
                  >{{ trainer.first_name }} {{ trainer.last_name }}</option
                >
              </b-form-select>
            </div>
            <div id="student-grade" class="mt-4">
              <label for="student-grade">Student grade at date:</label>
              <b-form-select
                name="student-grade"
                type="text"
                v-model="tracking.grade"
              >
                <option disabled value>Select Grade</option>
                <option v-for="(grade, i) in grades" :key="i" :value="grade">{{
                  grade
                }}</option>
              </b-form-select>
            </div>
            <div id="comments" class="mt-4">
              <label for="comments">Comments:</label>
              <b-form-input
                type="text"
                id="tracking-comments"
                name="comments"
                :rows="3"
                v-model="tracking.comments"
              ></b-form-input>
            </div>
          </div>
        </b-col>
        <b-col cols="1"></b-col>
        <b-col cols="5">
          <div class="teacher-section">
            <div id="teacher-name" class="mt-4">
              <label for="teacher-name">Teacher Name:</label>
              <b-form-input
                name="teacher-name"
                type="text"
                v-model="tracking.teacher_name"
              ></b-form-input>
            </div>
            <div id="teacher-mobile" class="mt-4">
              <label for="teacher-mobile">Teacher Mobile:</label>
              <b-form-input
                name="teacher-mobile"
                type="text"
                v-model="tracking.teacher_mobile"
                v-validate="mobileValidation"
                @keyup.enter="emitValue(!required)"
              ></b-form-input>
              <p v-if="errors.has('teacher-mobile')">
                {{ errors.first("teacher-mobile") }}
              </p>
            </div>
            <div id="ht-name" class="mt-4">
              <label for="ht-name">Head Teacher Name:</label>
              <b-form-input
                name="ht-name"
                type="text"
                v-model="tracking.head_teacher_name"
              ></b-form-input>
            </div>
            <div id="ht-mobile" class="mt-4">
              <label for="ht-mobile">Head Teacher Mobile:</label>
              <b-form-input
                name="ht-mobile"
                type="text"
                v-model="tracking.head_teacher_mobile"
                v-validate="mobileValidation"
                @keyup.enter="emitValue(!required)"
              ></b-form-input>
              <p v-if="errors.has('ht-mobile')">
                {{ errors.first("ht-mobile") }}
              </p>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div class="school-section">
            <div id="school-name" class="mt-4">
              <label for="school-name">Current School Name:</label>
              <b-form-input
                name="school-name"
                type="text"
                v-model="tracking.school_name"
              ></b-form-input>
            </div>
            <div id="school-type" class="mt-4">
              <label for="school-type">School Type:</label>
              <b-form-select name="school-type" v-model="tracking.school_type">
                <option disabled value>Select a School Type</option>
                <option value="Government">Government</option>
                <option value="Private">Private</option>
              </b-form-select>
            </div>
          </div>
          <div id="geo">
            <geo-info
              singular="tracking"
              plural="trackings"
              :coords="true"
              parent="tracking"
            ></geo-info>
          </div>
        </b-col>
      </b-row>
      <b-button :variant="'primary'" @click="addOrUpdate" class="m-4">{{
        buttonTitle
      }}</b-button>
    </div>
    <div>
      <b-row>
        <div id="tracking-table">
          <b-table
            v-cloak
            responsive
            :fields="fields"
            :items="studentTrackings"
            :striped="true"
            :bordered="true"
            :hover="true"
            stacked="md"
          >
            <template slot="cell(edit)" slot-scope="row">
              <b-link @click.stop="editTracking(row)" class="edit">Edit</b-link>
            </template>
            <template slot="cell(del)" slot-scope="row">
              <b-link @click.stop="deleteTracking(row)" class="delete"
                >DELETE</b-link
              >
            </template>
          </b-table>
        </div>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import { mapActions, mapMutations } from "vuex";
import TrackingService from "../services/TrackingService";
import { flash } from "@/mixins/flashes";
import { scrollTo } from "@/mixins/scrollTo";
import GeoInfo from "@/components/GeoInfo";

export default {
  name: "StudentTracking",
  props: {
    firstName: String,
    lastName: String,
    school: String,
  },
  mixins: [flash, scrollTo],
  components: {
    GeoInfo,
  },
  data() {
    return {
      fields: [
        { key: "date", label: "Date", sortable: true },
        { key: "grade", label: "Grade", sortable: true },
        { key: "school_name", label: "School", sortable: true },
        { key: "teacher_name", label: "Teacher", sortable: true },
        { key: "school_region", label: "School Region", sortable: true },
        { key: "school_district", label: "School District", sortable: true },
        { key: "school_village", label: "School Village", sortable: true },
        { key: "edit", label: "Edit", sortable: true },
        { key: "del", label: "DELETE", sortable: true },
      ],
      tracking: {
        id: null,
        trainer_id: null,
        student_id: this.$route.params.id,
        date: "",
        school_name: "",
        school_type: "",
        teacher_name: "",
        teacher_mobile: "",
        head_teacher_name: "",
        head_teacher_mobile: "",
        comments: "",
        grade: "",
      },
      showForm: false,
      disabled: false,
      required: false,
      mobileValidation: "numeric|min:9|max:10",
      grades: this.$store.state.grades,
      changeTitle: false,
      formError: false,
    };
  },
  computed: {
    showTitle() {
      return this.last_name.length;
    },
    buttonTitle() {
      return this.changeTitle ? "Edit Record" : "Submit Record";
    },
    trainers: {
      get() {
        return this.$store.state.trainers.trainers;
      },
    },
    studentTrackings: {
      get() {
        return this.$store.state.trackings.trackings;
      },
    },
  },
  methods: {
    ...mapActions({
      GET_TRACKINGS: "trackings/GET_STUDENT_TRACKINGS",
    }),
    ...mapMutations({
      setCountry: "trackings/country",
      setRegion: "trackings/region",
      setDistrict: "trackings/district",
      setVillage: "trackings/village",
      setAddress: "trackings/address",
      setLat: "trackings/lat",
      setLng: "trackings/lng",
    }),
    stop() {},
    async addTracking() {
      if (!this.tracking.date.length) {
        alert("Please provide a date the tracking was completed");
        this.formError = true;
        this.flashError();
        return;
      }
      const payload = this.tracking;
      await delete this.tracking.id;
      payload.school_country = this.$store.state.trackings.tracking.country;
      payload.school_region = this.$store.state.trackings.tracking.region;
      payload.school_district = this.$store.state.trackings.tracking.district;
      payload.school_village = this.$store.state.trackings.tracking.village;
      payload.school_address = this.$store.state.trackings.tracking.address;
      payload.school_lat = this.$store.state.trackings.tracking.lat;
      payload.school_lng = this.$store.state.trackings.tracking.lng;
      await TrackingService.addTrackingRecord(payload);
      await this.scrollTo("tracking-alert");
      await this.flashSuccess();
      this.clearForm();
      this.formError = false;
      this.changeTitle = false;
      this.showForm = false;
      await this.GET_TRACKINGS();
    },
    clearForm() {
      this.tracking.id = null;
      this.tracking.date = "";
      this.tracking.trainer_id = null;
      this.tracking.school_name = "";
      this.tracking.school_type = "";
      this.tracking.teacher_name = "";
      this.tracking.teacher_mobile = "";
      this.tracking.head_teacher_name = "";
      this.tracking.head_teacher_mobile = "";
      this.tracking.comments = "";
      this.tracking.grade = "";
    },
    editTracking(row) {
      this.tracking.id = row.item.id;
      this.tracking.date = row.item.date;
      this.tracking.trainer_id = row.item.trainer_id;
      this.tracking.school_name = row.item.school_name;
      this.tracking.school_type = row.item.school_type;
      this.tracking.teacher_name = row.item.teacher_name;
      this.tracking.teacher_mobile = row.item.teacher_mobile;
      this.tracking.head_teacher_name = row.item.teacher_name;
      this.tracking.head_teacher_mobile = row.item.teacher_mobile;
      this.tracking.comments = row.item.comments;
      this.tracking.grade = row.item.grade;
      this.setCountry(row.item.school_country);
      this.setRegion(row.item.school_region);
      this.setDistrict(row.item.school_district);
      this.setVillage(row.item.school_village);
      this.setAddress(row.item.school_address);
      this.setLat(row.item.school_lat);
      this.setLng(row.item.school_lng);
      this.showForm = true;
      this.changeTitle = true;
    },
    async updateTracking() {
      if (!this.tracking.date.length) {
        this.formError = true;
        this.flashError();
      } else {
        const payload = this.tracking;
        payload.school_country = this.$store.state.trackings.tracking.country;
        payload.school_region = this.$store.state.trackings.tracking.region;
        payload.school_district = this.$store.state.trackings.tracking.district;
        payload.school_village = this.$store.state.trackings.tracking.village;
        payload.school_address = this.$store.state.trackings.tracking.address;
        payload.school_lat = this.$store.state.trackings.tracking.lat;
        payload.school_lng = this.$store.state.trackings.tracking.lng;

        console.log("before update: ", payload);

        await TrackingService.updateTrackingRecord(payload);
        await this.scrollTo("tracking-alert");
        await this.flashSuccess();
        this.clearForm();
        this.formError = false;
        this.changeTitle = false;
        await this.GET_TRACKINGS();
        this.showForm = false;
      }
    },
    async deleteTracking(row) {
      const confirmDelete = confirm(
        "Are you sure you want to delete this tracking record?"
      );
      if (confirmDelete) {
        await TrackingService.deleteTrackingRecord(row.item.id);
        await this.GET_TRACKINGS();
      }
    },
    async addOrUpdate() {
      if (this.buttonTitle === "Edit Record") {
        this.updateTracking();
      } else {
        this.addTracking();
      }
    },
  },
  mounted() {
    this.GET_TRACKINGS();
  },
};
</script>

<style scoped>
div.col-5,
div.col {
  margin: 1%;
  padding: 1%;
  border: 1px solid gray;
}
</style>
